import { useCallback } from 'react';
import { toast } from 'sonner';
import { useUpdateProject } from '../../../hooks/useProjects';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { ProjectSharingSection } from '../ProjectSharingSection';
import { ClientReviewDefaults, type ClientReviewDefaultsValues } from '../ClientReviewDefaults';
import { log } from '@/lib/logger';

interface SharingTabProps {
  project: { id: string; teamId: string; visibility?: 'private' | 'team' | null } & ClientReviewDefaultsValues;
}

/**
 * The owner-only Sharing tab of the Project Settings modal. Moved from the
 * retired settings page (IssuesPageSettings.tsx) on 2026-09-17; who sees the tab
 * lives in projectSettingsTabs.ts.
 */
export function SharingTab({ project }: SharingTabProps) {
  const updateProject = useUpdateProject();
  // Resolves the caller's internal DB id (/users/me), so the sharing dropdown
  // can exclude the owner — sharing with yourself is a server 400.
  const currentUser = useCurrentUser();

  const handleVisibilityChange = useCallback(async (visibility: 'private' | 'team') => {
    log.info('[SharingTab] Updating project visibility', { projectId: project.id, visibility });
    try {
      await updateProject.mutateAsync({ id: project.id, data: { visibility } });
    } catch (err) {
      log.error('[SharingTab] Failed to update project visibility', { projectId: project.id, error: err instanceof Error ? err.message : String(err) });
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to update visibility. Please try again.');
    }
  }, [project.id, updateProject]);

  const handleDefaultsSave = useCallback(async (patch: ClientReviewDefaultsValues) => {
    log.info('[SharingTab] Updating client review defaults', { projectId: project.id, keys: Object.keys(patch) });
    try {
      await updateProject.mutateAsync({ id: project.id, data: patch });
    } catch (err) {
      log.error('[SharingTab] Failed to update client review defaults', { projectId: project.id, error: err instanceof Error ? err.message : String(err) });
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to save. Please try again.');
    }
  }, [project.id, updateProject]);

  return (
    <div className="px-6 pt-4 pb-5">
      <ProjectSharingSection
        projectId={project.id}
        visibility={project.visibility ?? 'private'}
        teamId={project.teamId}
        currentUserId={currentUser.data?.id}
        currentUserLoading={currentUser.isLoading}
        onVisibilityChange={handleVisibilityChange}
        visibilityUpdating={updateProject.isPending}
      >
        <ClientReviewDefaults project={project} onSave={handleDefaultsSave} disabled={updateProject.isPending} />
      </ProjectSharingSection>
    </div>
  );
}
